import DesignTokens from '@tableau/design-tokens';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location} from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { imgPath, imgUrl } from '~src/commonInfra/utils/ImgUrl';
import {
  sendHideConductorAwarenessTelemetry, sendLearnMoreConductorAwarenessTelemetry
} from '~src/flow/components/ConductorAwarenessTelemetry';
import { FlowSubplaceProps } from '~src/flow/types/FlowSubplaceProps';
import { sendDisableConductorAwarenessRequest } from '~src/flow/utils/PrepConductorAwarenessUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

const rootClass = 'FlowSchedulesInformationalContent';
const wrapper = cssClass(rootClass, 'wrapper', {
  display: 'flex',
  padding: 40,
  gap: 40,
});
const imageContent = cssClass(rootClass, 'imageContent', {
  width: 550,
});
const shadow = cssClass(rootClass, 'shadow', {
  boxShadow: '0 0 10px '+ applyColor(DesignTokens.color.feedback.default.neutral_dark, 'rgba(0, 0, 0, 0.2)'),
});
const textContent = cssClass(rootClass, 'textContent', {
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 380,
});
const header = cssClass(rootClass, 'header', {
  fontSize: 18,
  paddingBottom: 12,
});
const getListWrapper = () => cssClass(rootClass, 'listWrapper', {
  listStyleImage: imgUrl('check_12px-xsmall.svg'),
  paddingInlineStart: 20,
  $nest: {
    'li': {
      padding: '8px 4px',
    }
  }
});
const buttonWrapper = cssClass(rootClass, 'buttonWrapper', {
  display: 'flex',
  gap: 8
});

export function FlowSchedulesInformationalContent(subplaceProps: FlowSubplaceProps): JSX.Element {
  const onLearnMore = () => {
    sendLearnMoreConductorAwarenessTelemetry();
    window.open(HelpService.getServerHelpUrlByCategory('prep-dm-aware'), '_blank');
  };

  const onDismiss = () => {
    disablePrepConductorAwarenessSetting(subplaceProps);
    sendHideConductorAwarenessTelemetry();
  };

  function disablePrepConductorAwarenessSetting(subplaceProps: FlowSubplaceProps) {
    const siteUrls = subplaceProps.siteUrls;
    const flowUrl = siteUrls.flowUrl(subplaceProps.flowDetailInfo.id);

    sendDisableConductorAwarenessRequest();
    Location.instance.setUrl(flowUrl);
    _.defer(() => {
      showDisableAwarenessContentToast(subplaceProps);
    });
  }

  const showDisableAwarenessContentToast = (subplaceProps: FlowSubplaceProps) => {
    const currentUser = getSessionManagementServiceInstance().sessionUserInfo();
    const siteUrls = subplaceProps.siteUrls;
    const url = siteUrls.userSettingsUrl(currentUser.username, currentUser.domainName);

    ToasterService.instance.pop({
      type: ToastType.Info,
      text: Messages.flowSchedulesInformationalHideToastSuccess(),
      actionMessage: Messages.flowSchedulesInformationalHideViewSettings(),
      action: () => { Location.instance.setUrl(url); }
    });
  };

  return (
    <div className={wrapper}>
      <div className={imageContent}>
        <img width={550} className={shadow} alt={Messages.flowSchedulesInformationalContentHeader()} src={imgPath('ConductorAwareness.gif')}/>
      </div>
      <div className={textContent}>
        <div className={header}>{Messages.flowSchedulesInformationalContentHeader()}</div>
        <div>{Messages.flowSchedulesInformationalListLabel()}</div>
        <ul className={getListWrapper()}>
          <li>{Messages.flowSchedulesInformationalListItem1()}</li>
          <li>{Messages.flowSchedulesInformationalListItem2()}</li>
          <li>{Messages.flowSchedulesInformationalListItem3()}</li>
          <li>{Messages.flowSchedulesInformationalListItem4()}</li>
          <li>{Messages.flowSchedulesInformationalListItem5()}</li>
        </ul>
        <div className={buttonWrapper}>
          <ButtonWidget onClick={onLearnMore} buttonType={ButtonType.Primary}>{Messages.learnMore()}</ButtonWidget>
          <ButtonWidget onClick={onDismiss} buttonType={ButtonType.LowEmphasis}>{Messages.flowSchedulesInformationalNoThanks()}</ButtonWidget>
        </div>
      </div>
    </div>
  );
}